<template>
  <b-card id="containerInformation" class="p-sm-4 m-sm-4 my-4">
    <h5 v-if="circledNum" class="circled-number">{{ circledNum }}</h5>
    <b-row>
      <b-col md="6">
        <h5 class="card-title pt-2">Container Information</h5>
        <cool-label>Select Container Type</cool-label>
        <p class="small">Select how you would like us to make the ad behave on the page. Alternatively, if your page already has a container with special behavior, select the 'simple' container type and describe your ad container in the 'auction as type' field.</p>
        <b-card-group deck class="mt-4" id="containerTypeButtons">
          <div
            v-for="type in containerTypes"
            :id="'containerType' + type.value"
            :key="type.value"
            @click="$emit('update:containerType', type.value)"
            :class="containerType == type.value ? '' : 'text-muted'"
            class="text-center pt-2 col-lg-4 col-md-6">
            <b-card no-body
              :border-variant="containerType == type.value ? 'primary' : ''"
              :class="containerType == type.value ? 'card-info-below' : ''"
              class="card-button cursor-pointer py-3 px-1 m-0">
              <img :src="containerType == type.value ? type.iconActive : type.icon">
            </b-card>
            <h6 class="pt-sm-2">{{ type.name }}</h6>
          </div>
        </b-card-group>
        <b-row v-if="containerType === 'SIMPLE'">
          <b-col lg="6">
            <b-form-group class="my-2">
              <cool-label>
                Auction As Type
                <span v-b-popover.hover.click.right="'If you already have a docked or sticky ad container on your page and would prefer to use that, select the type of your container so that it can be auctioned appropriately.'"><span class="icon icon-Tooltip h5 d-inline align-text-bottom"></span></span>
              </cool-label>
              <b-form-select 
                :value="asType" 
                @input="$emit('update:asType', $event)" 
                :options="asTypeOptions" />
            </b-form-group>
          </b-col>
        </b-row>
        <template v-if="containerType === 'DOCKED'">
          <cool-label class="my-3">
            Select Container Placement
            <span v-b-popover.hover.click.right="'Should the ad container be at the top or bottom of the user\'s viewport?'"><span class="icon icon-Tooltip h5 d-inline align-text-bottom"></span></span>
          </cool-label>
          <b-row>
            <b-col lg="6">
              <b-form-group class="mb-3">
                <b-form-select 
                  :value="containerPlacement" 
                  @input="$emit('update:containerPlacement', $event)"
                  :options="containerPlacementOptions" />
              </b-form-group>
            </b-col>
          </b-row>
        </template>
        <hr>
        <cool-label class="my-3">
          Position on Page
          <span v-b-popover.hover.click.right="'Select the option that most closely describes the location of the zone on your page.'"><span class="icon icon-Tooltip h5 d-inline align-text-bottom"></span></span>
        </cool-label>
        <b-row>
          <b-col lg="6">
            <b-form-group>
              <b-form-select 
                :value="positionOnPage" 
                @input="$emit('update:positionOnPage', $event)" 
                :options="positionOnPageOptions" />
            </b-form-group>
          </b-col>
        </b-row>
      </b-col>
      <b-col md="6">
        <cool-label>Ad Render</cool-label>
        <b-card no-body bg-variant="lighter" class="ad-render">
          <ad-render
            :container-type="containerType"
            :position="containerPlacement">  
          </ad-render>
        </b-card>
      </b-col>
    </b-row>
  </b-card>
</template>

<style>
.card-button:hover {
  border-color: #DDD;
  transition: 0.1s;
}
.ad-render {
  width: 100%;
  height: 100%;
  max-height: 400px;
}
.card-info-below:after {
  content:"";
  position: absolute;
  bottom: -45px;
  left: 50%;
  transform: translate(-50%, 0);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 10px 12px 10px;
  border-color: transparent transparent var(--primary) transparent;
}
.infoBox {
  width: 100%;
  flex: 0 1 auto !important;
}
</style>

<script>
import AdRender from '@/components/AdRender'

export default{
  props: {
    circledNum: { required: false, type: String },    
    containerType: { required: true, type: String },
    containerPlacement: { required: true, type: String },
    positionOnPage: { required: true, type: String },
    asType: { required: true }
  },
  data () {
    return {
      containerPlacementOptions: [
        { value: '', text: 'Select Placement', disabled: true },
        { value: 'TOP', text: 'Top' },
        { value: 'BOTTOM', text: 'Bottom' }
      ],
      positionOnPageOptions: [
        { value: 'ATF', text: 'Above the fold' },
        { value: 'BTF', text: 'Below the fold' },
        { value: 'HEADER', text: 'Header' },
        { value: 'FOOTER', text: 'Footer' },
        { value: 'SIDEBAR', text: 'Sidebar' },
        { value: 'FULLSCREEN', text: 'Fullscreen' }
      ],
      containerTypes: [
        { value: 'SIMPLE', name: 'Simple', icon: require('./img/Simple.svg'), iconActive: require('./img/Simple_Active.svg'), infoTitle: 'Simple Ad Container:', info: 'The ad container will stay in the same place on the page, like the rest of your content.' },
        { value: 'DOCKED', name: 'Docked', icon: require('./img/Docked.svg'), iconActive: require('./img/Docked_Active.svg'), infoTitle: 'Docked Ad Container:', info: "The ad container will be docked to the bottom or top of the user's viewport, so it will always be in view." },
        { value: 'STICKY', name: 'Sticky', icon: require('./img/Sticky.svg'), iconActive: require('./img/Sticky_Active.svg'), infoTitle: 'Sticky Ad Container:', info: "The ad container will stay in the same spot on the page until the user begins to scroll past it, at which point it will adhere to the top or bottom of the user's screen." }
      ],
      asTypeOptions: [
        { value: null, text: 'Simple' },
        { value: 'DOCKED', text: 'Docked' },
        { value: 'STICKY', text: 'Sticky' },
      ]
    }
  },
  watch: {
    containerType () {
      this.addContainerInfoBox(this.containerType)
    }
  },
  methods: {
    //add info box below container type button
    addContainerInfoBox: function (typeValue) {
      let oldInfoBox = document.getElementById('containerTypeInfoBox');
      //first, remove current info box
      if(oldInfoBox) {
        oldInfoBox.outerHTML = "";
      }
      //get reference to first element of next line
      //(or null if last line)
      function findReference(currentNode) {
        let originalTop = currentNode.offsetTop; // get the clicked item offsetTop

        do {
          currentNode = currentNode.nextSibling; // get next sibling
        } while (currentNode !== null && (currentNode.nodeType !== 1 
        || currentNode.offsetTop === originalTop)); // keep iterating until null (last line) or a node with a different offsetTop (next line)

        return currentNode;
      }
      
      let type = this.containerTypes.find( type => type.value === typeValue )
      //create info box
      let label = document.createElement('div');
      label.className = 'text-uppercase font-weight-bold'
      label.innerHTML = type.infoTitle;
      let infoText = document.createElement('div');
      infoText.innerHTML = type.info;
      let infoBox = document.createElement('div');
      infoBox.className = 'card bg-primary border-primary text-white infoBox p-2 my-2 small';
      infoBox.id = 'containerTypeInfoBox';
      infoBox.appendChild(label);
      infoBox.appendChild(infoText);

      //find the first node of the next line
      let clickedButton = document.getElementById('containerType' + type.value);
      let nextLineFirstNode = findReference(clickedButton);

      //either insert infoBox before the next line starts, or insert it at the end
      let parent = document.getElementById('containerTypeButtons');
      if(nextLineFirstNode) {
        parent.insertBefore(infoBox, nextLineFirstNode);
      } else {
        parent.appendChild(infoBox);
      }
    }
  },
  components: {
    AdRender
  }
}

</script>


// WEBPACK FOOTER //
// src/publisher/pages/Zone/ZoneSettings/ContainerInformation.vue